import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SearchableCountryCodeDropdown from '../components/ui/SearchableCountryCodeDropdown';
import FastAPIClient from '../services/apiClient';
import logo_1024_X_1024 from '../assets/logo_1024_X_1024.png'
import InputWithPrepend from '../components/ui/InputWithPrepend';
import Modal from '../components/ui/Modal';

/**
 * Business Customer Signup Page.
 */
const SignupBusinessPage = () => {
    const navigate = useNavigate();
    const client = new FastAPIClient();

    const location = useLocation();
    const incomingState = location.state || {};

    const [isBusinessTypeModalOpen, setIsBusinessTypeModalOpen] = useState(!incomingState.btype);
    const [selectedBusinessType, setSelectedBusinessType] = useState(incomingState.btype || null);

    const [formData, setFormData] = useState({
        username: incomingState.username ?? '',
        password: '',
        confirm_password: '',
        business_name: incomingState.business_name ?? '',
        country_code: incomingState.country_code ?? '+234', // Default to Nigeria
        phone_number: incomingState.phone_number ?? '',
        btype: incomingState.btype ?? 'SRV',
    });
    const [errors, setErrors] = useState({});
    const [submitStatus, setSubmitStatus] = useState('idle');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const muralStyle = {
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 86c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zm66-3c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zm-46-43c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zm20-27c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1zm58 48c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1zm-24-20c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1zM44 55c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1zm6-20c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z' fill='%239f46e5' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E")`,
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        if (errors[name]) {
            setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
        }
    };

    const validateForm = () => {
        let newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        // Basic phone number regex (10-15 digits, allows spaces/dashes/parentheses)
        const phoneRegex = /^[\d\s\-\(\)]{8,15}$/;
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+=\-\[\]{};':"\\|,.<>\/?`~])[A-Za-z\d!@#$%^&*()_+=\-\[\]{};':"\\|,.<>\/?`~]{8,}$/;

        if (!formData.btype) {
            newErrors.general = 'Please select your business type first.'
        }

        if (!formData.username) {
            newErrors.username = 'Email is required.';
        } else if (!emailRegex.test(formData.username)) {
            newErrors.username = 'Invalid email format.';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required.';
        } else if (!passwordRegex.test(formData.password)) {
            newErrors.password = 'Password must be at least 8 characters, include uppercase, lowercase, number, and special character.';
        }

        if (!formData.confirm_password) {
            newErrors.confirm_password = 'Confirm password is required.';
        } else if (formData.password !== formData.confirm_password) {
            newErrors.confirm_password = 'Passwords do not match.';
        }

        if (!formData.business_name) newErrors.business_name = 'Legal Business Name is required.';

        // Phone number validation
        if (!formData.phone_number) {
            newErrors.phone_number = 'Phone Number is required.';
        } else if (!phoneRegex.test(formData.phone_number)) {
            newErrors.phone_number = 'Invalid phone number format.';
        }


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('loading');
        setErrors({});

        if (!validateForm()) {
            setSubmitStatus('error');
            setErrors(prev => ({ ...prev, general: 'Please correct the errors in the form.' }));
            return;
        }

        try {
            const fullPhoneNumber = formData.country_code + formData.phone_number.replace(/\s/g, '');

            const response = await client.signup(
                formData.username,
                formData.password,
                formData.business_name,
                fullPhoneNumber,
                formData.btype,
            );

            console.log('Registration successful:', response);
            setSubmitStatus('success');
            setTimeout(() => {
                navigate("/login", { state: { username: formData.username } });
            }, 2000);
        } catch (error) {
            console.error('Registration error:', error);
            setSubmitStatus('error');
            const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'An unexpected error occurred during registration.';
            setErrors(prev => ({ ...prev, general: errorMessage }));
        }
    };

    // Use the new searchable dropdown component here
    const CountryCodePrepend = (
        <SearchableCountryCodeDropdown
            value={formData.country_code}
            onChange={handleChange}
            error={errors.phone_number} // Pass error state for visual rhyme
        />
    );


    return (
        <div className="relative flex items-center justify-center min-h-screen bg-gray-50 p-4 overflow-hidden" style={muralStyle}>
            <div className="absolute inset-0 bg-gradient-to-t from-indigo-50/50 via-transparent to-transparent pointer-events-none" />
            <Card className="relative z-10 w-full max-w-md mx-auto p-8 shadow-xl">
                {/* ... (Logo and Headings) ... */}
                <div className="flex justify-center mb-6">
                    <img
                        src={logo_1024_X_1024}
                        alt="Arxis Logo"
                        className="w-24 h-24 object-contain rounded-lg select-none pointer-events-none"
                        onContextMenu={(e) => e.preventDefault()}
                        draggable="false"
                    />
                </div>

                <h2 className="text-2xl font-bold text-center text-text-primary mb-6">
                    Register Your Business
                </h2>
                <p className="text-center text-text-secondary mb-6 text-sm">
                    Join Arxis Accounting Web to manage your finances and inventory.
                </p>


                <form onSubmit={handleSubmit} noValidate>
                    {/* Email, Password, Confirm Password */}
                    <Input
                        label="Email Address (Your Username)"
                        id="username"
                        name="username"
                        type="email"
                        placeholder="e.g., yourbusiness@example.com"
                        value={formData.username}
                        onChange={handleChange}
                        required
                        error={errors.username}
                    />
                    {errors.username && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors.username}</p>}

                    <Input
                        label="Password"
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Minimum 8 characters, strong password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        error={errors.password}
                        trailingIcon={
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none pointer-events-auto"
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                        <path d="M1 1l22 22" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                )}
                            </button>
                        }
                    />
                    {errors.password && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors.password}</p>}

                    <Input
                        label="Confirm Password"
                        id="confirm_password"
                        name="confirm_password"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Re-enter your password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        required
                        error={errors.confirm_password}
                        trailingIcon={
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none pointer-events-auto"
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                                {showConfirmPassword ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                        <path d="M1 1l22 22" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                )}
                            </button>
                        }
                    />
                    {errors.confirm_password && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors.confirm_password}</p>}

                    {/* NEW PHONE NUMBER INPUT using the SearchableCountryCodeDropdown */}
                    <div className="mb-4 ml-1">
                        <InputWithPrepend
                            label="Phone Number"
                            id="phone_number"
                            name="phone_number"
                            type="tel"
                            placeholder="e.g., 801 234 5678"
                            value={formData.phone_number}
                            onChange={handleChange}
                            required
                            error={errors.phone_number}
                            prepend={CountryCodePrepend} // Pass the fully styled searchable dropdown
                            className="pl-2"
                        />
                        {errors.phone_number && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors.phone_number}</p>}
                    </div>

                    <div className="border-t border-gray-medium pt-4 mt-4 mb-6">
                        <p className="text-sm font-semibold text-text-secondary mb-3">Legal Business Name</p>
                        <Input
                            label="Your business name"
                            id="business_name"
                            name="business_name"
                            type="text"
                            placeholder="Full legal name of your business"
                            value={formData.business_name}
                            onChange={handleChange}
                            required
                            error={errors.business_name}
                            containerClassName="mb-3"
                        />
                        {errors.business_name && <p className="text-gmail-red text-xs mt-[-10px] mb-3">{errors.business_name}</p>}
                    </div>


                    {/* Submit Status and Button */}
                    {submitStatus === 'error' && errors.general && (
                        <p className="text-gmail-red text-sm text-center mb-4">{errors.general}</p>
                    )}
                    {submitStatus === 'success' && (
                        <p className="text-gmail-green text-sm text-center mb-4">
                            Registration successful! Redirecting to login...
                        </p>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        disabled={submitStatus === 'loading' || !formData.btype}
                        className="mt-4"
                    >
                        {submitStatus === 'loading' ? 'Registering Business...' : 'Register Business'}
                    </Button>
                </form>

                <p className="text-center text-sm text-text-secondary mt-6">
                    Already have an account?{' '}
                    <Link to="/login" className="text-gmail-blue hover:underline focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gmail-blue rounded-sm">
                        Login here
                    </Link>
                </p>
            </Card>
            <Modal isOpen={isBusinessTypeModalOpen}
                onClose={() => { }}
                title="What type of business are you registering?"
                size="sm"
            >
                <div className="space-y-6">
                    <p className="text-center text-text-secondary mb-4">
                        Configure Arxis Accounting for your industry.
                    </p>
                </div>
                <div className="grid grid-cols-1 gap-4">
                    <button
                        onClick={() => {
                            setSelectedBusinessType('EDU');
                            setFormData(prev => ({ ...prev, btype: 'EDU' }));
                            setIsBusinessTypeModalOpen(false);
                        }}
                        className="p-6 rounded-lg border-2 border-gray-300 hover:border-gmail-blue transition-all text-left hover:bg-blue-50"
                    >
                        <h4 className="font-semibold text-lg mb-2">Education</h4>
                        <p className="text-sm text-text-secondary">
                            Schools, universities, training centers, tutoring services, etc.
                        </p>
                    </button>

                    <button
                        onClick={() => {
                            setSelectedBusinessType('SRV'); // Changed key to 'SRV' for Services
                            setFormData(prev => ({ ...prev, btype: 'SRV' }));
                            setIsBusinessTypeModalOpen(false);
                        }}
                        className="p-6 rounded-lg border-2 border-gray-300 hover:border-gmail-blue transition-all text-left hover:bg-blue-50"
                    >
                        <h4 className="font-semibold text-lg mb-2">Services</h4>
                        <p className="text-sm text-text-secondary">
                            Consulting, agencies, freelancers, maintenance, and professional providers.
                        </p>
                    </button>
                </div>
                {/* <p className="text-xs text-text-secondary text-center">
                    You can change this later in settings if needed.
                </p> */}
            </Modal>
        </div>
    );
};

export default SignupBusinessPage;